import { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

/**
 * Horizontal chip strip with left/right scroll buttons, mirroring the
 * `variant="scrollable" scrollButtons="auto"` behaviour of the MUI Tabs used in
 * FleetRaceTabsv2. The buttons only appear once the chips overflow, and each one
 * fades out when there is nothing left to scroll in that direction.
 */
const ScrollableChipRow = ({ children, sx }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 1px tolerance: fractional widths otherwise leave the arrow permanently active
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateArrows();

    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    window.addEventListener("resize", updateArrows);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, children]);

  const handleScroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    // Same feel as the Tabs buttons: roughly one "page" of chips per click
    const distance = Math.max(120, el.clientWidth * 0.75);
    el.scrollBy({ left: direction * distance, behavior: "smooth" });
  };

  const hasOverflow = canScrollLeft || canScrollRight;

  const arrowSx = (enabled) => ({
    flexShrink: 0,
    padding: "2px",
    color: "#356dad",
    opacity: enabled ? 1 : 0.3,
    pointerEvents: enabled ? "auto" : "none",
    transition: "opacity 0.2s",
    "&:hover": { backgroundColor: "rgba(53,109,173,0.08)" },
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        maxWidth: "100%",
        marginBottom: "8px",
        ...sx,
      }}
    >
      {hasOverflow && (
        <IconButton
          size="small"
          aria-label="scroll left"
          onClick={() => handleScroll(-1)}
          sx={arrowSx(canScrollLeft)}
        >
          <KeyboardArrowLeftIcon fontSize="small" />
        </IconButton>
      )}

      <Box
        ref={scrollRef}
        onScroll={updateArrows}
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          gap: "8px",
          justifyContent: "left",
          flex: 1,
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& > *": { flexShrink: 0 },
        }}
      >
        {children}
      </Box>

      {hasOverflow && (
        <IconButton
          size="small"
          aria-label="scroll right"
          onClick={() => handleScroll(1)}
          sx={arrowSx(canScrollRight)}
        >
          <KeyboardArrowRightIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default ScrollableChipRow;
